<script>
  import BPMNModeler from "bpmn-js/lib/Modeler.js";
  import "bpmn-js/dist/assets/bpmn-js.css";
  import "bpmn-js/dist/assets/diagram-js.css";
  import "bpmn-js/dist/assets/bpmn-font/css/bpmn-embedded.css";
  import { onMount, onDestroy, createEventDispatcher } from "svelte";
  import { Button } from "paxray-components-svelte";
  import { fetchWithSession } from "lib/util/fetchWithSession.js";
  import { mirrorOrientation } from "lib/util/bpmnOrientation.js";
  import { CANCEL_ICON, DOWNLOAD_ICON, SAVE_ICON } from "@utils/icons.js";
  export let bpmnXml = "";
  export let saving = false;
  export let saved = false;
  export let filenameBase = "process";
  export let exportFormat = "standard";
  export let orientation = "horizontal";
  export let bicIdentifier = "";
  export let bicValidTo = "";
  export let bicChangeLog = "";
  export let bicGenerateTexts = false;
  // Flughoehe der erzeugten Texte: "task" fuer den Process Explorer (eine
  // Aufgabe mit ihren Schritten), "process" fuer den Case-Export.
  export let textLevel = "task";
  export let activityMetrics = [];
  export let connectionMetrics = [];
  export let processTimeUnit = "minutes";

  const dispatch = createEventDispatcher();

  let viewdiv;
  let modeler;
  let downloadPending = false;
  let appliedOrientation = "horizontal";
  let orientationPending = false;
  let diagramLoaded = false;

  function initModeler() {
    modeler = new BPMNModeler({
      container: viewdiv,
      keyboard: { bindTo: document },
    });
  }

  async function loadXml(xml) {
    if (!modeler || !xml) return;
    try {
      // Frisch erzeugtes XML ist immer horizontal; bei Bedarf gleich drehen,
      // damit ein Diagrammwechsel die gewaehlte Ausrichtung beibehaelt.
      const target = orientation;
      await modeler.importXML(
        target === "vertical" ? mirrorOrientation(xml) : xml,
      );
      appliedOrientation = target;
      diagramLoaded = true;
      const canvas = modeler.get("canvas");
      canvas.zoom("fit-viewport");
    } catch (err) {
      console.error("Failed to render BPMN diagram:", err);
    }
  }

  /**
   * Dreht den aktuell angezeigten Stand. Bewusst aus dem Modeler heraus und
   * nicht aus bpmnXml, damit Verschiebungen des Nutzers erhalten bleiben.
   */
  async function applyOrientation(target) {
    // Erst drehen, wenn ein Diagramm geladen ist: sonst scheitert saveXML mit
    // "no definitions loaded". Faellt auf, wenn die Ausrichtung schon vor dem
    // ersten Import auf vertikal steht, wie beim Export aus dem Object Explorer.
    if (!modeler || !diagramLoaded || orientationPending) return;
    if (target === appliedOrientation) return;
    orientationPending = true;
    try {
      const { xml } = await modeler.saveXML({ format: true });
      await modeler.importXML(mirrorOrientation(xml));
      modeler.get("canvas").zoom("fit-viewport");
      appliedOrientation = target;
    } catch (err) {
      console.error("Failed to change BPMN orientation:", err);
    } finally {
      orientationPending = false;
    }
  }

  $: if (modeler && bpmnXml) loadXml(bpmnXml);
  $: if (modeler && diagramLoaded) applyOrientation(orientation);

  function cleanXmlText(value) {
    return String(value || "").replace(
      /[\u0000-\u0008\u000b\u000c\u000e-\u001f\ud800-\udfff\ufffe\uffff]/g,
      "",
    );
  }

  function downloadUtf8(content, filename, mimeType) {
    const blob = new Blob([new TextEncoder().encode(cleanXmlText(content))], {
      type: `${mimeType};charset=utf-8`,
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  }

  function exportFilename(extension) {
    const safeName = String(filenameBase || "process")
      .replace(/[<>:"/\\|?*\u0000-\u001f]/g, "-")
      .replace(/\s+/g, " ")
      .replace(/[. ]+$/g, "")
      .trim();
    return `${safeName || "process"}.${extension}`;
  }

  export async function downloadBPMNFile() {
    if (!modeler || downloadPending) return;
    downloadPending = true;
    try {
      // Der Modeler haelt bereits die gewaehlte Ausrichtung - was man sieht,
      // wird exportiert, auch fuer den BIC-Weg.
      const result = await modeler.saveXML({ format: true });
      let xml = result.xml;
      let filename = exportFilename("bpmn");
      if (exportFormat === "bic") {
        const response = await fetchWithSession(
          "/api/damprojects/exportBicBPMN",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            suppressErrorToast: true,
            body: JSON.stringify({
              xml,
              activity_metrics: activityMetrics,
              connection_metrics: connectionMetrics,
              process_time_unit: processTimeUnit,
              identifier: bicIdentifier,
              valid_to: bicValidTo,
              change_log: bicChangeLog,
              generate_texts: bicGenerateTexts,
              text_level: textLevel,
            }),
          },
        );
        if (!response.ok) {
          let message = `BIC export failed with status ${response.status}`;
          try {
            const error = await response.json();
            message = error.message || message;
          } catch {
            // Keep the status-based fallback for non-JSON responses.
          }
          throw new Error(message);
        }
        xml = await response.text();
        filename = exportFilename("bic.bpmn");
      }
      downloadUtf8(xml, filename, "application/xml");
      fetchWithSession("/api/damprojects/user-history/activity", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ event: "model" }),
      }).catch(() => {});
    } catch (error) {
      dispatch("download-error", {
        message: error?.message || "The BPMN file could not be downloaded.",
      });
    } finally {
      downloadPending = false;
    }
  }

  export async function downloadSVG() {
    if (!modeler) return;
    const result = await modeler.saveSVG();
    downloadUtf8(result.svg, exportFilename("svg"), "image/svg+xml");
  }

  async function saveBPMN() {
    if (!modeler || saving) return;
    const result = await modeler.saveXML({ format: true });
    dispatch("save", { xml: result.xml });
  }

  export function zoomIn() {
    if (!modeler) return;
    const zoomScroll = modeler.get("zoomScroll");
    zoomScroll.stepZoom(1);
  }

  export function zoomOut() {
    if (!modeler) return;
    const zoomScroll = modeler.get("zoomScroll");
    zoomScroll.stepZoom(-1);
  }

  export function resetZoom() {
    if (!modeler) return;
    const canvas = modeler.get("canvas");
    canvas.zoom("fit-viewport");
  }

  onMount(() => {
    initModeler();
  });

  onDestroy(() => {
    if (modeler) modeler.destroy();
  });
</script>

<div class="bpmn-viewer-wrapper">
  <div class="bpmn-viewer-toolbar">
    <Button
      icon={SAVE_ICON}
      iconLeft
      kind="secondary"
      text={saving ? "Saving..." : saved ? "Saved" : "Save BPMN"}
      disabled={saving}
      on:click={saveBPMN}
    />
    <Button
      icon={DOWNLOAD_ICON}
      iconLeft
      kind="secondary"
      text={downloadPending
        ? "Preparing..."
        : exportFormat === "bic"
          ? "Download BIC BPMN"
          : "Download BPMN"}
      disabled={downloadPending}
      on:click={downloadBPMNFile}
    />
    <Button
      icon={DOWNLOAD_ICON}
      iconLeft
      text="Download SVG"
      kind="secondary"
      on:click={downloadSVG}
    />
    <Button
      iconLeft
      kind="secondary"
      icon={CANCEL_ICON}
      on:click={() => dispatch("close")}
    />
  </div>
  <div class="bpmn-viewer-canvas" bind:this={viewdiv} />
</div>

<style>
  .bpmn-viewer-wrapper {
    position: relative;
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
  }

  .bpmn-viewer-toolbar {
    position: absolute;
    top: 1rem;
    right: 1rem;
    z-index: 10;
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
  }

  .bpmn-viewer-canvas {
    flex: 1;
    border-radius: 0.5rem;
    background-color: var(--pax-white, #fff);
    background-image: radial-gradient(#d6d9de 1px, transparent 1px);
    background-size: 16px 16px;
    margin: 0;
  }

  :global(.bpmn-viewer-wrapper .djs-palette) {
    top: 1rem !important;
  }

  :global(.bpmn-viewer-wrapper svg:focus) {
    outline: none;
  }

  :global(.bpmn-viewer-wrapper .bjs-powered-by) {
    display: none !important;
  }
</style>
